import { getAllClientConfigs, listClients } from '@arcanumai/client-config';
import { Client, ClientConfig } from '@/types';
import { awsCredentialsService } from './awsCredentialsService';
import { portalAdmin } from './portalAdmin';
import { clientConfigSchema } from '@/types';
import { validateClientName } from '@/utils/clientValidation';

/** The reason a write failed, as one sentence for the page. */
function errorText(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Unknown error.';
}

export class ClientService {
  private cachedClients: Client[] = [];
  private lastFetchTime: number = 0;
  private readonly cacheDuration = 5 * 60 * 1000; // 5 minutes

  /** The page's AWS credentials, or undefined before the portal config has loaded. */
  private getCredentialsProvider() {
    return awsCredentialsService.credentialsIfConfigured();
  }

  async getAllClients(): Promise<Client[]> {
    const now = Date.now();
    if (this.cachedClients.length > 0 && now - this.lastFetchTime < this.cacheDuration) {
      return this.cachedClients;
    }

    try {
      const credentials = this.getCredentialsProvider();
      const [clientNames, clientConfigs] = await Promise.all([
        awsCredentialsService.retryIfRevoked(() => listClients({ credentials })),
        awsCredentialsService.retryIfRevoked(() => getAllClientConfigs<ClientConfig>({ credentials })),
      ]);

      this.cachedClients = clientNames.map((name) => {
        const config = clientConfigs[name];
        if (!config) {
          return {
            name,
            config: {
              clientAccountId: 'unknown',
              region: 'us-east-1',
              allApps: false,
              devInstance: name.includes('demo') || name.includes('dev'),
              provisionQResources: false,
              preferredKnowledgeBase: 'bedrock',
            },
            status: 'pending' as const,
            lastDeployment: undefined,
            deploymentCount: 0,
          };
        }

        return {
          name,
          config,
          status: this.determineClientStatus(name, config),
          lastDeployment: undefined, // TODO: Implement deployment history
          deploymentCount: 0, // TODO: Implement deployment counting
        };
      });

      this.lastFetchTime = now;
      return this.cachedClients;
    } catch (error) {
      console.error('Failed to fetch client data:', error);
      throw new Error('Unable to load client configurations. Please check your AWS credentials and permissions.');
    }
  }

  async getClient(name: string): Promise<Client | undefined> {
    const clients = await this.getAllClients();
    return clients.find((client) => client.name === name);
  }

  private determineClientStatus(_name: string, _config: ClientConfig): Client['status'] {
    // All client configurations are intentional - default to healthy
    // Status should only indicate actual deployment/system issues, not configuration choices
    return 'healthy';
  }

  /**
   * Changes the named settings of one client and nothing else, through the
   * portal-admin Lambda (TASK-630). A setting given as `undefined` is
   * removed. The Lambda writes the activity row with the before and after.
   */
  async updateClientConfig(name: string, configUpdates: Partial<ClientConfig>): Promise<void> {
    const nameError = validateClientName(name);
    if (nameError) {
      throw new Error(`Invalid client name: ${nameError}`);
    }

    const set: Record<string, unknown> = {};
    const remove: string[] = [];
    for (const [key, value] of Object.entries(configUpdates)) {
      if (value === undefined) remove.push(key);
      else set[key] = value;
    }
    if (Object.keys(set).length === 0 && remove.length === 0) return;

    try {
      await portalAdmin('clientConfig.update', { clientName: name, set, remove });
    } catch (error) {
      console.error(`Failed to update client config for ${name}:`, error);
      throw new Error(`Unable to update client configuration. ${errorText(error)}`);
    } finally {
      this.clearCache();
    }
  }

  /** Saves a whole config in place of the one there. The portal checks it against its schema first. */
  async replaceClientConfig(name: string, newConfig: ClientConfig): Promise<void> {
    const nameError = validateClientName(name);
    if (nameError) {
      throw new Error(`Invalid client name: ${nameError}`);
    }

    try {
      clientConfigSchema.strict().parse(newConfig);
      await portalAdmin('clientConfig.replace', { clientName: name, config: newConfig });
    } catch (error) {
      console.error(`Failed to replace client config for ${name}:`, error);
      throw new Error(`Unable to replace client configuration. ${errorText(error)}`);
    } finally {
      this.clearCache();
    }
  }

  /** Saves a new client's config. A name that already has a config gets this one in its place. */
  async createClientConfig(name: string, config: ClientConfig): Promise<void> {
    const nameError = validateClientName(name);
    if (nameError) {
      throw new Error(`Invalid client name: ${nameError}`);
    }

    try {
      clientConfigSchema.parse(config);
      await portalAdmin('clientConfig.create', { clientName: name, config });
    } catch (error) {
      console.error(`Failed to create client config for ${name}:`, error);
      throw new Error(`Unable to create client configuration. ${errorText(error)}`);
    } finally {
      this.clearCache();
    }
  }

  async deleteClientConfig(name: string): Promise<void> {
    try {
      await portalAdmin('clientConfig.delete', { clientName: name });
    } catch (error) {
      console.error(`Failed to delete client config for ${name}:`, error);
      throw new Error(`Unable to delete client configuration. ${errorText(error)}`);
    } finally {
      this.clearCache();
    }
  }

  clearCache(): void {
    this.cachedClients = [];
    this.lastFetchTime = 0;
  }
}

export const clientService = new ClientService();

// Client grouping utilities
export interface GroupedClients {
  devClients: Client[];
  productionClients: Client[];
}

export function groupClientsByType(clients: Client[]): GroupedClients {
  const devClients = clients.filter((c) => c.config.devInstance === true).sort((a, b) => a.name.localeCompare(b.name));

  const productionClients = clients
    .filter((c) => c.config.devInstance !== true)
    .sort((a, b) => a.name.localeCompare(b.name));

  return { devClients, productionClients };
}
